/**
 * EQ 均衡器状态管理 Store
 * 
 * 功能:
 * - 管理 14 频点的 EQ 设置
 * - 支持多种预设模式（游戏、音乐、电影等）
 * - 支持自定义 EQ 曲线保存
 * - 与设备 HID 通信发送 EQ 命令
 * 
 * 对齐原项目 js/eq_setting.js 的逻辑
 */
import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { hidManager } from '@/api/device/hid'
import {
  buildEqSinglePointCommand,
  buildEqCurveCommands,
  type EqDataPoint,
} from '@/api/device/commands'
import type { EqMode, EqPresetData } from '@/api/eq'
import {
  getEqList,
  addEqPreset,
  removeEqPreset,
  pinEqPreset,
  updateEqPoint,
} from '@/api/eq'
import { useUserStore } from './user'

/**
 * EQ 常数定义
 * 对齐原项目 eq_setting.js
 */

/**
 * 14 个 EQ 频点
 */
const DEFAULT_FREQUENCIES = [
  '20Hz', '60Hz', '120Hz', '250Hz', '500Hz',
  '800Hz', '1K', '2K', '4K', '6K',
  '8K', '10K', '12K', '20K'
]

// Default Q value
const DEFAULT_Q = '0.75'

// Max custom presets limit
const MAX_CUSTOM_PRESETS = 20

// System preset IDs for each mode
const SYSTEM_PRESET_IDS: Record<EqMode, string[]> = {
  game: ['wwqy', 'cs', 'apex', 'pubg', 'CF', 'sjz'],
  music: ['Pop Mode', 'Bass+', 'Vocal Focus', 'DJ Mode'],
  movie: ['Theater Surround', 'Horror Atmosphere', 'Sci-Fi Space'],
}

// Create default EQ point
function createDefaultPoint(index: number): EqDataPoint {
  return {
    fid: index + 1,
    freq: DEFAULT_FREQUENCIES[index] || '',
    gain: 0,
    q: DEFAULT_Q,
  }
}

// Create zero curve (14 points at 0 gain)
function createZeroCurve(): EqDataPoint[] {
  return Array.from({ length: 14 }, (_, i) => createDefaultPoint(i))
}

// Default system presets data aligned with eq_setting.js
function getDefaultSystemPresets(mode: EqMode): EqPresetData[] {
  const presets: Record<EqMode, EqPresetData[]> = {
    game: [
      {
        id: 'wwqy',
        name: '无畏契约',
        datapoint: [
          { fid: 1, freq: '25HZ', gain: 0, q: '0.75' },
          { fid: 2, freq: '50HZ', gain: -5, q: '0.75' },
          { fid: 3, freq: '75HZ', gain: -4, q: '0.75' },
          { fid: 4, freq: '150HZ', gain: 0, q: '0.75' },
          { fid: 5, freq: '300HZ', gain: 6, q: '0.75' },
          { fid: 6, freq: '600HZ', gain: -1, q: '0.75' },
          { fid: 7, freq: '1K', gain: -1, q: '0.75' },
          { fid: 8, freq: '2K', gain: 6, q: '0.75' },
          { fid: 9, freq: '3K', gain: -3, q: '0.75' },
          { fid: 10, freq: '6K', gain: -2, q: '0.75' },
          { fid: 11, freq: '8K', gain: 2, q: '0.75' },
          { fid: 12, freq: '10K', gain: -3, q: '0.75' },
          { fid: 13, freq: '12K', gain: 1, q: '0.75' },
          { fid: 14, freq: '16K', gain: 0, q: '0.75' },
        ],
      },
      {
        id: 'cs',
        name: 'CS:GO',
        datapoint: [
          { fid: 1, freq: '25HZ', gain: 0, q: '0.75' },
          { fid: 2, freq: '50HZ', gain: 6, q: '0.75' },
          { fid: 3, freq: '75HZ', gain: -6, q: '0.75' },
          { fid: 4, freq: '150HZ', gain: 6, q: '0.75' },
          { fid: 5, freq: '300HZ', gain: -2, q: '0.75' },
          { fid: 6, freq: '600HZ', gain: 3, q: '0.75' },
          { fid: 7, freq: '1K', gain: 1, q: '0.75' },
          { fid: 8, freq: '2K', gain: 2, q: '0.75' },
          { fid: 9, freq: '3K', gain: 3, q: '0.75' },
          { fid: 10, freq: '6K', gain: 0, q: '0.75' },
          { fid: 11, freq: '8K', gain: 2, q: '0.75' },
          { fid: 12, freq: '10K', gain: -3, q: '0.75' },
          { fid: 13, freq: '12K', gain: -4, q: '0.75' },
          { fid: 14, freq: '16K', gain: 0, q: '0.75' },
        ],
      },
    ],
    music: [
      {
        id: 'Pop Mode',
        name: '流行歌曲',
        datapoint: [
          { fid: 1, freq: '25HZ', gain: 0, q: '0.75' },
          { fid: 2, freq: '50HZ', gain: -1, q: '0.75' },
          { fid: 3, freq: '75HZ', gain: -6, q: '0.75' },
          { fid: 4, freq: '150HZ', gain: 2.5, q: '0.75' },
          { fid: 5, freq: '300HZ', gain: -5, q: '0.75' },
          { fid: 6, freq: '600HZ', gain: 1, q: '0.75' },
          { fid: 7, freq: '1K', gain: 0.5, q: '0.75' },
          { fid: 8, freq: '2K', gain: 0, q: '0.75' },
          { fid: 9, freq: '3K', gain: -0.5, q: '0.75' },
          { fid: 10, freq: '6K', gain: 0, q: '0.75' },
          { fid: 11, freq: '8K', gain: 0, q: '0.75' },
          { fid: 12, freq: '10K', gain: 5, q: '0.75' },
          { fid: 13, freq: '12K', gain: 0, q: '0.75' },
          { fid: 14, freq: '16K', gain: 0, q: '0.75' },
        ],
      },
    ],
    movie: [
      {
        id: 'Theater Surround',
        name: '影视音效',
        datapoint: [
          { fid: 1, freq: '25HZ', gain: 0, q: '0.75' },
          { fid: 2, freq: '50HZ', gain: 0, q: '0.75' },
          { fid: 3, freq: '75HZ', gain: 1.5, q: '0.75' },
          { fid: 4, freq: '150HZ', gain: 5, q: '0.75' },
          { fid: 5, freq: '300HZ', gain: -1, q: '0.75' },
          { fid: 6, freq: '600HZ', gain: -1, q: '0.75' },
          { fid: 7, freq: '1K', gain: 1, q: '0.75' },
          { fid: 8, freq: '2K', gain: -1, q: '0.75' },
          { fid: 9, freq: '3K', gain: 0, q: '0.75' },
          { fid: 10, freq: '6K', gain: -1.5, q: '0.75' },
          { fid: 11, freq: '8K', gain: 1, q: '0.75' },
          { fid: 12, freq: '10K', gain: 1, q: '0.75' },
          { fid: 13, freq: '12K', gain: 2.5, q: '0.75' },
          { fid: 14, freq: '16K', gain: 0, q: '0.75' },
        ],
      },
    ],
  }

  return presets[mode] || []
}

// ============ Store Types ============

interface EqModeState {
  enabled: boolean
  currentPreset: string
  systemPresets: EqPresetData[]
  customPresets: EqPresetData[]
  lastPreset: string | null
  pinnedPreset: string | null
}

export const useEqStore = defineStore('eq', () => {
  // ============ State ============

  const gameEq = ref<EqModeState>({
    enabled: false,
    currentPreset: 'wwqy',
    systemPresets: getDefaultSystemPresets('game'),
    customPresets: [],
    lastPreset: null,
    pinnedPreset: null,
  })

  const musicEq = ref<EqModeState>({
    enabled: false,
    currentPreset: 'Pop Mode',
    systemPresets: getDefaultSystemPresets('music'),
    customPresets: [],
    lastPreset: null,
    pinnedPreset: null,
  })

  const movieEq = ref<EqModeState>({
    enabled: false,
    currentPreset: 'Theater Surround',
    systemPresets: getDefaultSystemPresets('movie'),
    customPresets: [],
    lastPreset: null,
    pinnedPreset: null,
  })

  const activeMode = ref<EqMode>('game')

  // Track if data has been loaded from server
  const remoteLoaded = ref<Record<EqMode, boolean>>({
    game: false,
    music: false,
    movie: false,
  })

  // Debounce timers for API calls
  const apiDebounceTimers = ref<Record<string, ReturnType<typeof setTimeout>>>({})

  // ============ Getters ============

  const currentModeState = computed(() => {
    switch (activeMode.value) {
      case 'game': return gameEq.value
      case 'music': return musicEq.value
      case 'movie': return movieEq.value
    }
  })

  /** 兼容 GameEq / MusicEq / MovieEq 等旧命名 */
  const currentSettings = currentModeState

  const allPresets = computed(() => {
    const state = currentModeState.value
    return [...state.systemPresets, ...state.customPresets]
  })

  const currentPreset = computed(() => {
    return allPresets.value.find(p => p.id === currentModeState.value.currentPreset)
  })

  const currentPresetData = computed((): EqDataPoint[] => {
    return currentPreset.value?.datapoint || createZeroCurve()
  })

  const isSystemPreset = computed(() => (presetId: string) => {
    return SYSTEM_PRESET_IDS[activeMode.value].includes(presetId)
  })

  const canAddMoreCustom = computed(() => {
    return currentModeState.value.customPresets.length < MAX_CUSTOM_PRESETS
  })

  // 兼容BottomMenu的getter别名
  const isEnabled = computed(() => currentModeState.value?.enabled || false)
  const currentMode = computed(() => activeMode.value)

  // ============ Helper Functions ============

  function getModeState(mode: EqMode): EqModeState {
    switch (mode) {
      case 'game': return gameEq.value
      case 'music': return musicEq.value
      case 'movie': return movieEq.value
    }
  }

  function isLoggedIn(): boolean {
    try {
      const userStore = useUserStore()
      return userStore.isLoggedIn && !!userStore.uuid
    } catch {
      return false
    }
  }

  function getUuid(): string {
    try {
      const userStore = useUserStore()
      return userStore.uuid || ''
    } catch {
      return ''
    }
  }

  // ============ Actions ============

  function setActiveMode(mode: EqMode) {
    activeMode.value = mode
  }

  /** 兼容 AI 助手 / 首页 */
  const switchMode = setActiveMode

  async function setEqEnabled(enabled: boolean) {
    currentModeState.value.enabled = enabled
    if (enabled) {
      // 对齐旧版: 开启EQ前，如果"一键吃鸡"开着，会先关掉吃鸡（互斥）
      // 这里需要调用chiji store来关闭吃鸡
      try {
        const { useChijiStore } = await import('./chiji')
        const chijiStore = useChijiStore()
        if (chijiStore.enabled) {
          await chijiStore.setEnabled(false)
        }
      } catch {
        // ignore: chiji store might not be available in some builds
      }
      await applyCurrentEq()
    }
  }

  function selectPreset(presetId: string) {
    currentModeState.value.currentPreset = presetId
    currentModeState.value.lastPreset = presetId
  }

  /**
   * 更新EQ增益 - 单点实时调节
   * 对齐旧版: 单点包实时发送 + 防抖远端同步
   */
  async function updateEqGain(bandIndex: number, gain: number, skipDeviceSend = false) {
    const preset = currentPreset.value
    if (!preset || !preset.datapoint[bandIndex]) return

    // Update local state
    preset.datapoint[bandIndex].gain = gain

    // Send to device immediately (single point command)
    if (!skipDeviceSend && currentModeState.value.enabled) {
      const point = preset.datapoint[bandIndex]
      const command = buildEqSinglePointCommand(bandIndex, point)
      await hidManager.sendData(command, false)
    }

    // Debounced API sync for logged-in users
    if (isLoggedIn()) {
      debouncedSyncPointToApi(activeMode.value, preset.id, bandIndex, preset.datapoint[bandIndex])
    }
  }

  /**
   * 防抖同步单点数据到服务器
   */
  function debouncedSyncPointToApi(mode: EqMode, presetId: string, bandIndex: number, point: EqDataPoint) {
    const key = `${mode}-${presetId}-${bandIndex}`

    if (apiDebounceTimers.value[key]) {
      clearTimeout(apiDebounceTimers.value[key])
    }

    apiDebounceTimers.value[key] = setTimeout(async () => {
      const uuid = getUuid()
      if (!uuid) return

      try {
        await updateEqPoint(mode, uuid, presetId, point)
      } catch (err) {
        console.error('Failed to sync EQ point to API:', err)
      }

      delete apiDebounceTimers.value[key]
    }, 500) // 500ms debounce
  }

  /**
   * 应用当前EQ曲线 - 使用分段包协议
   * 对齐旧版: 14段按4个segment分段发送，每包间隔200ms
   */
  async function applyCurrentEq() {
    const preset = currentPreset.value
    if (!preset) return

    const commands = buildEqCurveCommands(preset.datapoint)

    // Send segments with 200ms delay (aligned with original)
    for (let i = 0; i < commands.length; i++) {
      await hidManager.sendData(commands[i], false)
      if (i < commands.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 200))
      }
    }
  }

  // ============ Custom Preset Management ============

  async function addCustomPreset(name: string, datapoint: EqDataPoint[]): Promise<string | null> {
    const state = currentModeState.value

    if (state.customPresets.length >= MAX_CUSTOM_PRESETS) {
      throw new Error('已达到自定义标签上限(20个)')
    }

    const newPreset: EqPresetData = {
      id: `custom_${Date.now()}`,
      name,
      datapoint: datapoint.map(p => ({ ...p })),
      isCustom: true,
      createdAt: Date.now(),
    }

    // Add to server if logged in
    if (isLoggedIn()) {
      const uuid = getUuid()
      try {
        const response = await addEqPreset(activeMode.value, uuid, newPreset)
        if (response.code === 1 && response.data?.id) {
          newPreset.id = response.data.id
        }
      } catch (err) {
        console.error('Failed to add preset to server:', err)
      }
    }

    state.customPresets.push(newPreset)
    return newPreset.id
  }

  async function deleteCustomPreset(presetId: string): Promise<boolean> {
    const state = currentModeState.value
    const index = state.customPresets.findIndex(p => p.id === presetId)

    if (index === -1) return false

    // Delete from server if logged in
    if (isLoggedIn()) {
      const uuid = getUuid()
      try {
        await removeEqPreset(activeMode.value, uuid, presetId)
      } catch (err) {
        console.error('Failed to remove preset from server:', err)
      }
    }

    state.customPresets.splice(index, 1)

    // Switch to another preset if current was deleted
    if (state.currentPreset === presetId) {
      state.currentPreset = state.systemPresets[0]?.id || ''
    }

    return true
  }

  async function pinPreset(presetId: string): Promise<boolean> {
    const state = currentModeState.value

    // Update on server if logged in
    if (isLoggedIn()) {
      const uuid = getUuid()
      try {
        const response = await pinEqPreset(activeMode.value, uuid, presetId)
        if (response.code !== 1) return false
      } catch (err) {
        console.error('Failed to pin preset:', err)
        return false
      }
    }

    state.pinnedPreset = presetId
    return true
  }

  // ============ Remote Data Loading ============

  /**
   * 从服务器加载EQ列表
   * 对齐旧版: 登录后才可持久化/同步
   */
  async function loadFromRemote(mode: EqMode): Promise<boolean> {
    if (!isLoggedIn()) {
      remoteLoaded.value[mode] = false
      return false
    }

    if (remoteLoaded.value[mode]) return true

    const uuid = getUuid()
    const state = getModeState(mode)

    try {
      const response = await getEqList(mode, uuid)

      if (response.code !== 1 || !response.data) {
        return false
      }

      const { presets, lastPreset, defaultPreset } = response.data

      // Update system presets
      const systemIds = SYSTEM_PRESET_IDS[mode]
      const serverSystemPresets = presets.filter(p => systemIds.includes(p.id))
      const serverCustomPresets = presets.filter(p => !systemIds.includes(p.id))

      if (serverSystemPresets.length > 0) {
        state.systemPresets = serverSystemPresets
      }

      // Update custom presets
      state.customPresets = serverCustomPresets.map((p, i) => ({
        ...p,
        isCustom: true,
        createdAt: p.createdAt || Date.now() + i,
      }))

      // Update last/pinned preset
      state.lastPreset = lastPreset || null
      state.pinnedPreset = defaultPreset || null

      // Set current preset
      if (lastPreset && [...state.systemPresets, ...state.customPresets].some(p => p.id === lastPreset)) {
        state.currentPreset = lastPreset
      }

      remoteLoaded.value[mode] = true
      return true
    } catch (err) {
      console.error(`Failed to load ${mode} EQ from remote:`, err)
      return false
    }
  }

  async function loadAllFromRemote(): Promise<void> {
    if (!isLoggedIn()) return

    await Promise.all([
      loadFromRemote('game'),
      loadFromRemote('music'),
      loadFromRemote('movie'),
    ])
  }

  // ============ Persistence ============

  function loadFromStorage() {
    // 对齐旧版: 未登录时不读取游客历史，清空缓存回默认
    if (!isLoggedIn()) {
      // Reset to defaults for guest users
      gameEq.value.customPresets = []
      musicEq.value.customPresets = []
      movieEq.value.customPresets = []
      return
    }

    try {
      const stored = localStorage.getItem('eqSettings')
      if (stored) {
        const parsed = JSON.parse(stored)
        if (parsed.activeMode) activeMode.value = parsed.activeMode
        // Restore mode-specific state if needed
      }
    } catch {
      console.warn('Failed to load EQ settings from storage')
    }
  }

  function saveToStorage() {
    if (!isLoggedIn()) return

    try {
      localStorage.setItem('eqSettings', JSON.stringify({
        activeMode: activeMode.value,
        lastUpdated: Date.now(),
      }))
    } catch {
      console.warn('Failed to save EQ settings to storage')
    }
  }

  // ============ Return ============

  return {
    // State
    gameEq,
    musicEq,
    movieEq,
    activeMode,
    remoteLoaded,
    // Getters
    currentModeState,
    currentSettings,
    allPresets,
    currentPreset,
    currentPresetData,
    isSystemPreset,
    canAddMoreCustom,
    isEnabled,
    currentMode,
    // Actions
    setActiveMode,
    switchMode,
    setEqEnabled,
    selectPreset,
    updateEqGain,
    applyCurrentEq,
    addCustomPreset,
    deleteCustomPreset,
    pinPreset,
    loadFromRemote,
    loadAllFromRemote,
    loadFromStorage,
    saveToStorage,
  }
})
